<template>
    <div class="cropSelector">
        <div class="cs-rect" :style="rectStyle" @mousedown="onBodyMouseDown">
            <!-- Remplir only: the part of the selection that actually survives (see resultStyle).
                 Ajuster keeps all of it, so there'd be nothing to mark - and the equivalent box
                 there (the output frame *around* the selection) can be several times the frame's
                 own size on a big format mismatch, sprawling across the editor. -->
            <div v-if="resultStyle" class="cs-result" :style="resultStyle"></div>
            <div class="cs-handle cs-tl" @mousedown.stop="e => onHandleMouseDown('tl', e)"></div>
            <div class="cs-handle cs-tr" @mousedown.stop="e => onHandleMouseDown('tr', e)"></div>
            <div class="cs-handle cs-bl" @mousedown.stop="e => onHandleMouseDown('bl', e)"></div>
            <div class="cs-handle cs-br" @mousedown.stop="e => onHandleMouseDown('br', e)"></div>
        </div>
    </div>
</template>

<script>
const MIN_SIZE = 0.05

export default {
    name: 'CropSelector',

    props: {
        clip: {
            type: Object,
            required: true
        },
        // The stage this selector is drawn over. While the crop tool is open it's shaped to the
        // source, not to the output format - see VideoPreviewCanvas.vue's cropWorkspaceSize.
        canvasSize: {
            type: Object,
            required: true
        },
        // The *output* frame's aspect ratio (timeline.canvas), which the stage above deliberately
        // no longer matches - needed to draw where that frame lands relative to the selection.
        outputRatio: {
            type: Number,
            required: true
        }
    },

    computed: {
        cropRect() {
            return this.clip.transform.cropRect || { x: 0, y: 0, width: 1, height: 1 }
        },

        fillMode() {
            return this.clip.transform.fillMode || 'contain'
        },

        // The video is shown "contain"-fit (full, letterboxed) for as long as the crop tool is open
        // - this is the resulting displayed box, in percentages of the stage.
        containBox() {
            const canvasRatio = this.canvasSize.width / this.canvasSize.height
            const sourceRatio = (this.clip.sourceWidth && this.clip.sourceHeight)
                ? this.clip.sourceWidth / this.clip.sourceHeight
                : canvasRatio

            let widthPct
            let heightPct
            if (sourceRatio > canvasRatio) {
                widthPct = 100
                heightPct = 100 * (canvasRatio / sourceRatio)
            } else {
                heightPct = 100
                widthPct = 100 * (sourceRatio / canvasRatio)
            }

            return {
                leftPct: (100 - widthPct) / 2,
                topPct: (100 - heightPct) / 2,
                widthPct,
                heightPct
            }
        },

        rectStyle() {
            const box = this.containBox
            const r = this.cropRect
            return {
                left: `${box.leftPct + r.x * box.widthPct}%`,
                top: `${box.topPct + r.y * box.heightPct}%`,
                width: `${r.width * box.widthPct}%`,
                height: `${r.height * box.heightPct}%`
            }
        },

        // Remplir/cover fills the output frame edge to edge, so anything in the selection that
        // doesn't match that frame's ratio gets cropped off a second time by drawMediaFrame - the
        // selection has to keep the output ratio for the box to mean what it shows. Ajuster/contain
        // letterboxes the whole selection instead, so any shape is drawn exactly as picked and free
        // resizing stays correct there. Locked against the *output* ratio rather than
        // aspectRatioTarget: that's the value drawMediaFrame actually cover-fits against.
        lockedRatio() {
            if (this.fillMode !== 'cover') return null
            if (typeof this.clip.aspectRatioTarget !== 'number') return null
            if (!this.clip.sourceWidth || !this.clip.sourceHeight) return null
            return this.outputRatio
        },

        // Remplir zooms the selection until it fills the output frame, so whatever in it doesn't
        // match that frame's ratio is cropped a second time - this is the largest output-shaped box
        // that fits inside the selection, centered (drawMediaFrame centers the drawn region), i.e.
        // exactly what survives. null in Ajuster, which keeps the whole selection.
        //
        // The stage is shaped to the source rather than to the output (see VideoPreviewCanvas.vue's
        // cropWorkspaceSize), so an output-shaped box isn't simply a square in percent-of-stage
        // terms: its two percentages are in the ratio outputRatio / stageRatio.
        resultStyle() {
            if (this.fillMode !== 'cover') return null

            const box = this.containBox
            const r = this.cropRect
            const w = r.width * box.widthPct
            const h = r.height * box.heightPct
            const stageRatio = this.canvasSize.width / this.canvasSize.height
            if (!w || !h || !stageRatio || !this.outputRatio) return null

            // Target width:height, both as percentages of the stage.
            const k = this.outputRatio / stageRatio
            // A selection wider than the target shape is bounded by its height, and vice versa.
            const heightBinds = w / h > k
            const widthPct = ((heightBinds ? h * k : w) / w) * 100
            const heightPct = ((heightBinds ? h : w / k) / h) * 100
            // With a preset active the selection is ratio-locked to the output already (see
            // lockedRatio), so this lands exactly on the white border - two coincident outlines read
            // as a rendering glitch rather than as information. Only worth drawing when the
            // selection's shape actually differs from the output's.
            if (widthPct > 99.5 && heightPct > 99.5) return null

            return {
                left: `${(100 - widthPct) / 2}%`,
                top: `${(100 - heightPct) / 2}%`,
                width: `${widthPct}%`,
                height: `${heightPct}%`
            }
        }
    },

    // Closing the crop tool (or switching clip) mid-drag destroys this component while its own
    // document-level listeners are still attached - they'd otherwise keep emitting into a dead
    // instance until the next mouseup.
    beforeDestroy() {
        this.detachDragListeners()
    },

    methods: {
        // Deltas are normalized to the *selection's* own 0-1 space: divided by the frame's on-screen
        // size, then by the fraction of that frame the source actually occupies (containBox).
        // stageRect/box are snapshotted at mousedown - neither can change mid-gesture, since
        // dragging no longer resizes the canvas (see MediaEditor.vue's onUpdateCropRect).
        startDrag(e, computeRect) {
            // .vpc-frame, not .vpc-stage: the latter only centers the frame and can be
            // wider/taller than it whenever the two don't share the same aspect ratio
            // (letterboxing), which would otherwise scale every drag delta incorrectly.
            const stage = e.target.closest('.vpc-frame')
            if (!stage) return

            const stageRect = stage.getBoundingClientRect()
            const box = this.containBox
            const startX = e.clientX
            const startY = e.clientY
            const original = { ...this.cropRect }
            let moved = false

            this.detachDragListeners()
            this.$emit('drag-start')

            this.onDragMove = (moveEvent) => {
                const dx = ((moveEvent.clientX - startX) / stageRect.width) * 100 / box.widthPct
                const dy = ((moveEvent.clientY - startY) / stageRect.height) * 100 / box.heightPct
                if (!moved && !dx && !dy) return
                moved = true
                this.$emit('update-crop-rect', computeRect(original, dx, dy))
            }
            this.onDragUp = () => {
                this.detachDragListeners()
                // drag-end before crop-commit: the former releases VideoPreviewCanvas's frozen
                // canvas size, and the latter is what may resize the canvas - the other way round
                // the new size would be frozen out until the next gesture.
                this.$emit('drag-end')
                // A click that never moved isn't an edit - committing it would push a pointless
                // undo entry for every stray click on the selection.
                if (moved) this.$emit('crop-commit')
            }
            document.addEventListener('mousemove', this.onDragMove)
            document.addEventListener('mouseup', this.onDragUp)
        },

        detachDragListeners() {
            if (this.onDragMove) document.removeEventListener('mousemove', this.onDragMove)
            if (this.onDragUp) document.removeEventListener('mouseup', this.onDragUp)
            this.onDragMove = null
            this.onDragUp = null
        },

        onBodyMouseDown(e) {
            this.startDrag(e, (original, dx, dy) => ({
                x: Math.min(Math.max(0, original.x + dx), 1 - original.width),
                y: Math.min(Math.max(0, original.y + dy), 1 - original.height),
                width: original.width,
                height: original.height
            }))
        },

        // Anchored on the *opposite* corner, which stays put for the whole gesture - the previous
        // version derived x/width and y/height independently, which can't express a ratio lock at
        // all (correcting one axis has to feed back into the other).
        onHandleMouseDown(corner, e) {
            const left = corner.includes('l')
            const top = corner.includes('t')

            this.startDrag(e, (original, dx, dy) => {
                const anchorX = left ? original.x + original.width : original.x
                const anchorY = top ? original.y + original.height : original.y
                const maxW = left ? anchorX : 1 - anchorX
                const maxH = top ? anchorY : 1 - anchorY

                const clamp = (value, max) => Math.min(Math.max(MIN_SIZE, value), Math.max(MIN_SIZE, max))
                let width = clamp(original.width + (left ? -dx : dx), maxW)
                let height = clamp(original.height + (top ? -dy : dy), maxH)

                const ratio = this.lockedRatio
                if (ratio) {
                    // cropRect's width/height are fractions of sourceWidth/sourceHeight
                    // *respectively*, so their raw quotient isn't the selection's pixel aspect ratio
                    // - the conversion has to go through real pixels both ways.
                    const sourceWidth = this.clip.sourceWidth
                    const sourceHeight = this.clip.sourceHeight
                    const heightFor = (w) => (w * sourceWidth / ratio) / sourceHeight
                    const widthFor = (h) => (h * sourceHeight * ratio) / sourceWidth

                    // Follow whichever axis was actually dragged furthest (compared in source
                    // pixels, since dx/dy are normalized against different denominators).
                    if (Math.abs(dx * sourceWidth) >= Math.abs(dy * sourceHeight)) {
                        height = heightFor(width)
                    } else {
                        width = widthFor(height)
                    }

                    // Each correction has to propagate back into the other axis or the lock breaks.
                    if (height > maxH) { height = maxH; width = widthFor(height) }
                    if (width > maxW) { width = maxW; height = heightFor(width) }
                    if (width < MIN_SIZE) { width = MIN_SIZE; height = heightFor(width) }
                    if (height < MIN_SIZE) { height = MIN_SIZE; width = widthFor(height) }
                }

                // Final clamp: MIN_SIZE wins over maxW/maxH above, so a handle dragged past the
                // opposite edge of an already-tiny selection could otherwise push it off-source.
                return {
                    x: Math.min(Math.max(0, left ? anchorX - width : anchorX), 1 - width),
                    y: Math.min(Math.max(0, top ? anchorY - height : anchorY), 1 - height),
                    width,
                    height
                }
            })
        }
    }
}
</script>

<style lang="scss" scoped></style>
